import type { Session } from '../components/types/Session';
import LocalStorageKeys from '../constants/LocalStorageKeys';
import { differenceInMonths, isSameDay } from './Helpers';
import Cookie from './Cookie';
import Cookies from '../constants/Cookies';

export default class SessionManager {
  #maximumSessions: number = 5;
  #maximumSessionDurationInMonths: number = 6;
  #sessions: Session[];

  /**
   * Create an instance of a session manager
   */
  constructor() {
    const today = new Date();

    let sessions = this.retrieveSessionsFromStorage().filter((session) => {
      return differenceInMonths(session.id, today) < this.#maximumSessionDurationInMonths;
    });

    const currentSession = sessions.find((session) => isSameDay(session.id, today));
    if (!currentSession) {
      sessions.unshift({
        id: today.valueOf(),
        recentlyVisitedProducts: []
      });

      if (sessions.length > this.#maximumSessions) {
        sessions = sessions.slice(0, this.#maximumSessions);
      }
    }

    this.#sessions = sessions;
    this.persistToStorage();
  }

  /**
   * Add a recently visited product to the session
   */
  addRecentlyVisitedProduct(id: string): boolean {
    if (!this.trackingAllowed() || !this.currentSession) return false;

    const product = this.currentSession.recentlyVisitedProducts.find((product) => product.id === id);
    if (product) {
      product.amount += 1;
      product.lastSeen = new Date();
    } else {
      this.currentSession.recentlyVisitedProducts.push({ id, amount: 1, lastSeen: new Date() });
    }

    this.persistToStorage();

    return true;
  }

  /**
   * Check whether tracking of the user is allowed
   */
  trackingAllowed(): boolean {
    return Cookie.get(Cookies.TRACKING) === '1';
  }

  /**
   * Clear all the tracking information
   */
  clearAllTrackingInformation(): void {
    this.#sessions = this.#sessions.map((session) => {
      session.recentlyVisitedProducts = [];
      return session;
    });
    this.persistToStorage();
  }

  /**
   * Retrieve all sessions from the storage
   */
  retrieveSessionsFromStorage(): Session[] {
    if (!this.storageIsAvailable()) return [];

    const storageData = localStorage.getItem(LocalStorageKeys.SESSIONS);
    return (storageData ? JSON.parse(storageData) : []).map((session) => {
      session.id = +session.id;
      return session;
    });
  }

  /**
   * Persist the sessions to the storage
   */
  persistToStorage(): void {
    if (!this.storageIsAvailable()) return;

    localStorage.setItem(LocalStorageKeys.SESSIONS, JSON.stringify(this.#sessions));
  }

  /**
   * Check if the storage is available
   */
  storageIsAvailable(): boolean {
    try {
      localStorage.setItem('storage-enabled', true);
      localStorage.removeItem('storage-enabled');
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Getter for the sessions property
   */
  get sessions(): Session[] {
    return this.#sessions;
  }

  /**
   * Getter for the current session
   */
  get currentSession(): ?Session {
    return this.#sessions.length ? this.#sessions[0] : null;
  }
}
